import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook that animates a number counting up from 0 to `end` when the
 * element scrolls into view. Returns [ref, displayValue].
 *
 * Handles the case where `end` starts as 0 (data loading) and later
 * updates to a real value — the animation will trigger once `end > 0`
 * and the element is in view.
 *
 * @param {number} end - Target number
 * @param {number} duration - Animation duration in ms (default 1500)
 * @param {string} suffix - Optional suffix like "+" (default "")
 */
const useCountUp = (end, duration = 1500, suffix = '') => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isVisibleRef = useRef(false);
  const hasAnimatedRef = useRef(false);

  const animate = useCallback((targetEnd) => {
    const endNum = typeof targetEnd === 'number' ? targetEnd : parseInt(targetEnd, 10) || 0;
    if (endNum <= 0) return;

    hasAnimatedRef.current = true;
    const startTime = performance.now();

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic for a smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * endNum);
      setCount(current);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [duration]);

  // IntersectionObserver — track visibility
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          isVisibleRef.current = true;
          // If we have a valid end value and haven't animated yet, go
          if (!hasAnimatedRef.current && end > 0) {
            animate(end);
          }
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [animate, end]);

  // When `end` changes (e.g. data finishes loading), animate if visible
  useEffect(() => {
    if (isVisibleRef.current && !hasAnimatedRef.current && end > 0) {
      animate(end);
    }
  }, [end, animate]);

  const displayValue = `${count}${suffix}`;
  return [ref, displayValue];
};

export default useCountUp;
